import { useEffect, useRef, useState } from 'react';
import type { Canceler } from 'axios';
import { API } from '../../../constants';
import { getRequestUrl, post } from '../../../utils';
import { SuiteId, useRefState } from '@libs/shared';
import {
    MessageChannel,
    MessageConfig,
    MessageConfigData,
    MessageConfigItem,
    MessageReceiverMap
} from '../interfaces';

interface MessageSettingType {
    label: string;
    key: string;
}

/**
 * 消息设置顶部的一级分类
 */
export const useMessageSettingTypes = () => {
    const [messageSettingTypes, setMessageSettingTypes] = useState<MessageSettingType[]>();

    const fetchMessageSettingTypes = (suiteId?: SuiteId) => {
        post<MessageSettingType[]>(getRequestUrl(API.message.get_config_menu), {
            suite_id: suiteId
        }).then((res) => {
            setMessageSettingTypes(res.data);
        });
    };

    return {
        isFetchingMessageSettingTypes: !messageSettingTypes,
        messageSettingTypes,
        fetchMessageSettingTypes
    };
};

/**
 * 获取消息设置中某个分类下的全部配置（包含所有子分类）
 */
export const useMessageConfig = () => {
    const [messageConfig, setMessageConfig] = useState<MessageConfig>();

    const fetchMessageConfig = (type: string) => {
        post<MessageConfig>(getRequestUrl(API.message.get_config), {
            type
        }).then((res) => {
            setMessageConfig(res.data);
        });
    };

    return {
        messageConfig,
        fetchMessageConfig
    };
};

/**
 * 获取所有消息接收者信息
 * https://fanruan-x.feishu.cn/wiki/TShjwURysim3kgkcXkzcRf2anLd 第9条
 */
export const useFetchReceiverMap = () => {
    const [receiverMap, setReceiverMap] = useState<MessageReceiverMap>();

    const fetchReceiverMap = () => {
        post<{ receiver_map: MessageReceiverMap }>(
            getRequestUrl(API.message.get_extra_receiver)
        ).then((res) => {
            setReceiverMap(res.data.receiver_map);
        });
    };

    return {
        receiverMap,
        setReceiverMap,
        fetchReceiverMap
    };
};

/**
 * 获取并更新消息设置中具体某一个子分类的配置
 * @param defaultConfig 默认配置
 */
export const useUpdateMessageConfig = (defaultConfig?: MessageConfigData) => {
    const [messageConfigData, setMessageConfigData, configRef] = useRefState<
        MessageConfigData | undefined
    >(defaultConfig);
    const cancelRef = useRef<Canceler>();

    // 切换 tab 或表格复用单元格时 defaultConfig 会变化，重置内部状态
    useEffect(() => {
        setMessageConfigData(defaultConfig);
    }, [defaultConfig]);

    /**
     * 更新单个消息渠道配置
     * - 先乐观更新 UI，再发起请求
     * - 新请求会 abort 上一个未完成的请求，保证落库的一定是最新的全量 config
     * - 请求被 abort 时静默忽略，只有真实网络/业务错误才回滚
     */
    const updateMessageConfig = (
        configId: string,
        channel: MessageChannel,
        channelConfig: MessageConfigItem
    ) => {
        const prev = configRef.current;
        if (!prev) {
            return Promise.resolve();
        }
        const config = { ...prev, [channel]: channelConfig };
        // 乐观更新 UI，后续并发操作能读到本次变更
        setMessageConfigData(config);
        // abort 上一个未完成的请求，避免旧请求返回后覆盖本次结果
        cancelRef.current?.();
        return post(
            getRequestUrl(API.message.update_config),
            {
                config_id: configId,
                config: config
            },
            {
                cancelExecutor: (cancel) => {
                    cancelRef.current = cancel;
                }
            }
        ).catch(() => {
            // 请求失败，回滚到修改前的状态
            setMessageConfigData(prev);
        });
    };

    return {
        messageConfigData,
        updateMessageConfig
    };
};
